/**
 * Wave 1 Disposal tab: the opt-in shaping a measure pivot needs to lay out
 * Zay's Disposal Tracker tables (MeasurePivotTable.tsx).
 *
 * Every piece is opt-in on the widget, so a pivot that declares none of them
 * (1.2 Revenue by Operation and every other existing one) renders exactly as
 * before:
 *
 *   rows_then   a second grouping column, read in the same server-side GROUP BY
 *               (Zay's driver and truck, month and facility)
 *   sort        {measure: <label>, direction}: order rows by a measure
 *               (Zay sorts divisions by cost, facilities by tickets)
 *   limit       keep the first N rows after sorting (his "top 15")
 *   coverage    on a ratio measure, {num, of, floor}: withhold the cell when
 *               the ratio's own numerator is under `floor` of `of` for that row
 *               (MER-720, the same rule a $/ton tile applies)
 *
 * Plain .mjs so node:test imports it with no build step.
 */

/** Stable group key for one or two grouping values. */
export function pivotKey(values) {
  return JSON.stringify(values.map((v) => (v === null || v === undefined ? "" : String(v))));
}

/** Inverse of pivotKey. */
export function pivotKeyParts(key) {
  try {
    const parsed = JSON.parse(key);
    return Array.isArray(parsed) ? parsed.map(String) : [String(key)];
  } catch {
    return [String(key)];
  }
}

/** The grouping columns a pivot widget declares, in order. */
export function pivotRowColumns(widget) {
  const cols = [];
  if (typeof widget?.rows === "string" && widget.rows) cols.push(widget.rows);
  if (typeof widget?.rows_then === "string" && widget.rows_then) cols.push(widget.rows_then);
  return cols;
}

function num(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Order and cap pivot groups.
 *
 * @param {{key: string, values: Record<string, number|null>}[]} groups
 * @param {{measure?: string, direction?: string} | undefined} sort
 * @param {number | undefined} limit
 * @returns the same groups, reordered and capped. Without a sort measure the
 *   order is the key order it always was; a missing value sorts last either
 *   way, so a withheld row never tops a ranking.
 */
export function orderPivotGroups(groups, sort, limit) {
  let out = [...groups];
  const measure = sort && typeof sort.measure === "string" ? sort.measure : null;
  if (measure) {
    const dir = sort.direction === "asc" ? 1 : -1;
    out.sort((a, b) => {
      const va = num(a.values[measure]);
      const vb = num(b.values[measure]);
      if (va === null && vb === null) return a.key.localeCompare(b.key);
      if (va === null) return 1;
      if (vb === null) return -1;
      if (va !== vb) return (va - vb) * dir;
      return a.key.localeCompare(b.key);
    });
  } else if (sort && sort.key_direction === "desc") {
    out.sort((a, b) => b.key.localeCompare(a.key));
  } else {
    out.sort((a, b) => a.key.localeCompare(b.key));
  }
  if (typeof limit === "number" && Number.isInteger(limit) && limit > 0) {
    out = out.slice(0, limit);
  }
  return out;
}

/**
 * MER-720 on one pivot cell: keep `value` only when the ratio's numerator is
 * at least `floor` of the row's whole. A row with no whole (no spend at all)
 * has nothing to withhold and keeps whatever the ratio already is.
 */
export function coverageGate(value, covered, whole, floor) {
  const v = num(value);
  if (v === null) return null;
  const w = num(whole);
  const f = num(floor);
  if (f === null || w === null || w <= 0) return v;
  const c = num(covered);
  if (c === null || c / w < f) return null;
  return v;
}

/**
 * The line a capped list shows above itself: never a silent truncation.
 * Only called when `total` exceeds `shown`.
 */
export function cappedListNote(shown, total) {
  return `Showing the newest ${shown.toLocaleString("en-US")} of ${total.toLocaleString("en-US")} rows in this period.`;
}

/**
 * PostgREST returns at most this many rows per request (the Supabase
 * max-rows setting). A grouped aggregate is one row per group, so a pivot
 * with more groups than this is silently cut: a top-N would rank an arbitrary
 * subset, and each measure is its own request, so two measures could be cut
 * at different groups (Tickets present, Tons blank on the same row).
 */
export const PIVOT_PAGE_SIZE = 1000;

/** The most groups a paged pivot will read before it refuses to rank. */
export const PIVOT_MAX_GROUPS = 20000;

/** Thrown when a pivot's groups cannot all be read; the table is withheld. */
export class PivotTruncatedError extends Error {
  constructor(message) {
    super(message);
    this.name = "PivotTruncatedError";
  }
}

export const PIVOT_TRUNCATED_COPY =
  "Too many rows to rank completely for this period. Narrow the period or a filter to see this table.";

/**
 * Read every group of a grouped aggregate, or refuse.
 *
 * @param {(from: number, to: number) => Promise<unknown[]>} fetchPage  one
 *   request for rows [from, to] under a stable order on the grouping columns
 * @param {{paged: boolean, pageSize?: number, maxGroups?: number}} opts
 *   paged=false: one request, and a full page is treated as cut (a pivot that
 *   never asked for paging cannot know what it lost). paged=true: pages until
 *   a short page, refusing past maxGroups.
 * @returns every row across the pages
 * @throws PivotTruncatedError when the complete set was not read
 */
export async function fetchAllGroups(fetchPage, opts) {
  const pageSize = opts.pageSize ?? PIVOT_PAGE_SIZE;
  const maxGroups = opts.maxGroups ?? PIVOT_MAX_GROUPS;
  if (!opts.paged) {
    const rows = await fetchPage(0, pageSize - 1);
    if (rows.length >= pageSize) {
      throw new PivotTruncatedError(`pivot read ${rows.length} groups, the server's row limit`);
    }
    return rows;
  }
  const all = [];
  for (let from = 0; ; from += pageSize) {
    if (from >= maxGroups) {
      throw new PivotTruncatedError(`pivot has more than ${maxGroups} groups`);
    }
    const rows = await fetchPage(from, from + pageSize - 1);
    all.push(...rows);
    if (rows.length < pageSize) return all;
  }
}
